import type { Microsoft365Feature } from '@dbrain/shared';
import { Alert, Button, Dialog, Icons } from '@dbrain/ui';
import { useEffect, useId, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useCompanyZone } from '../../api/hooks.js';
import { useMicrosoft365Connect, useMicrosoft365DevicePoll, useMicrosoft365Status, type Microsoft365DeviceStart } from '../../api/microsoft365.js';
import { CopyButton } from '../chat/Markdown.js';
import { ErrorAlert } from '../ErrorAlert.js';
import { ConsentRequestButton } from './parts.js';
import { connectPromises, openable, reasonKey, upgradePromises } from './state.js';

/**
 * "Hesabımı bağla" (MsBaglanOnay): what Oppy will and will not do with the person's Microsoft account, then
 * Microsoft's own sign-in page (the browser goes there and comes back to `returnTo`). On an install whose
 * address Entra does not accept (plain http), the firm picks "Kodla": the dialog shows a code to type on
 * Microsoft's page, opens that page in a new tab and waits; once signed in it closes itself and calls
 * `onConnected`. Oppy never sees the password and deletes nothing in Microsoft 365; with part 3's switches on it
 * promises to send and create only with the person's approval. With `features` it is the upgrade sign-in of
 * "Yeni izin gerekiyor · yeniden bağlan" (no mockup): only the new promises, and the connect names those features.
 */
export function Microsoft365ConnectDialog({
  returnTo,
  features: upgrade,
  onClose,
  onConnected,
  onError,
}: {
  /** a path of this app the sign-in comes back to (/connectors, /c/<id>?microsoft365=resume, /settings/account) */
  returnTo: string;
  /** an upgrade: the features whose permissions Microsoft asks for besides the ones the account has (never scopes) */
  features?: readonly Microsoft365Feature[];
  onClose: () => void;
  /** the code flow finished here (the redirect flow comes back through the address instead) */
  onConnected?: () => void;
  /** a start the server refused (the card shows "Kullanılamıyor" for microsoft_unavailable) */
  onError?: (error: unknown) => void;
}) {
  const { t } = useTranslation();
  const status = useMicrosoft365Status().data;
  const connect = useMicrosoft365Connect();
  const [device, setDevice] = useState<Microsoft365DeviceStart | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [badUrl, setBadUrl] = useState(false);
  const poll = useMicrosoft365DevicePoll(device);
  const done = useRef(false);
  const open = useRef<HTMLAnchorElement>(null);
  const again = useRef<HTMLButtonElement>(null);
  const handlers = useRef({ onClose, onConnected, onError });
  handlers.current = { onClose, onConnected, onError };
  const byCode = status?.signInMethod === 'device_code';
  const features = status?.features ?? READ_ONLY;
  const waiting = device !== null && poll.status === 'pending';
  const upgrading = upgrade !== undefined && upgrade.length > 0;

  const start = () => {
    setBadUrl(false);
    setDevice(null);
    connect.mutate(upgrading ? { returnTo, features: upgrade } : returnTo, {
      onSuccess: (result) => {
        if (result.kind === 'device') {
          setDevice({ id: result.id, userCode: result.userCode, verificationUri: result.verificationUri, expiresAt: result.expiresAt, interval: result.interval });
          return;
        }
        if (!openable(result.url)) {
          setBadUrl(true);
          return;
        }
        // the button stays busy while the browser leaves for Microsoft
        setLeaving(true);
        window.location.assign(result.url);
      },
      onError: (error) => handlers.current.onError?.(error),
    });
  };

  // signed in on Microsoft's page: the state was read again; the dialog's job is done
  useEffect(() => {
    if (poll.status !== 'connected' || done.current) return;
    done.current = true;
    handlers.current.onConnected?.();
    handlers.current.onClose();
  }, [poll.status]);

  // the button that was focused went away with the step: focus follows to the step's own button
  useEffect(() => {
    if (!device) return;
    if (waiting) open.current?.focus();
    else again.current?.focus();
  }, [device, waiting]);

  const footer = device ? (
    <>
      <Button variant="ghost" onClick={onClose} data-testid="microsoft365-cancel">
        {t('common.cancel')}
      </Button>
      {waiting ? (
        openable(device.verificationUri) ? (
          <a ref={open} className="ui-button ui-button--primary" href={device.verificationUri} target="_blank" rel="noopener noreferrer" data-testid="microsoft365-device-open">
            {t('microsoft365.user.dialog.device.open')}
            <Icons.external size={15} />
          </a>
        ) : null
      ) : (
        <Button ref={again} icon={<Icons.regenerate size={15} />} busy={connect.isPending} onClick={start} data-testid="microsoft365-device-again">
          {poll.status === 'expired' ? t('microsoft365.user.dialog.device.newCode') : t('common.retry')}
        </Button>
      )}
    </>
  ) : (
    <>
      <Button variant="ghost" onClick={onClose} data-testid="microsoft365-cancel">
        {t('common.cancel')}
      </Button>
      <Button busy={connect.isPending || leaving} onClick={start} data-testid="microsoft365-continue">
        {byCode ? t('microsoft365.user.dialog.continueDevice') : t('microsoft365.user.dialog.continueRedirect')}
        {byCode ? null : <Icons.external size={15} />}
      </Button>
    </>
  );

  return (
    <Dialog
      title={
        <span className="m365-dialog__title">
          <span className="m365-tile m365-tile--sm" aria-hidden="true">
            <Icons.connections size={18} />
          </span>
          {t(upgrading ? 'microsoft365.user.dialog.upgrade.title' : 'microsoft365.user.dialog.title')}
        </span>
      }
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={footer}
      initialFocus='[data-testid="microsoft365-continue"]'
      className="m365-dialog"
      data-testid="microsoft365-connect-dialog"
    >
      <p className="m365-dialog__subtitle" data-testid={upgrading ? 'microsoft365-upgrade' : undefined} data-features={upgrading ? upgrade.join(' ') : undefined}>
        {t(upgrading ? 'microsoft365.user.dialog.upgrade.subtitle' : 'microsoft365.user.dialog.subtitle')}
      </p>
      {device ? (
        <DeviceCode device={device} status={poll.status} reason={poll.reason} error={poll.error} />
      ) : (
        <Promises features={features} upgrade={upgrading ? upgrade : null} byCode={byCode} />
      )}
      {badUrl ? <Alert tone="error" title={t('microsoft365.user.dialog.badUrl')} /> : null}
      <ErrorAlert error={connect.error} />
    </Dialog>
  );
}

/** Before the state is read: part 2's reading promises only. */
const READ_ONLY: Record<Microsoft365Feature, boolean> = { mail_read: true, calendar_read: true, mail_send: false, calendar_write: false, onedrive_import: false, sharepoint_import: false };

/**
 * "Oppy şunları yapabilecek" / "Oppy şunları yapmaz" and where the connection can be removed again: what the firm has
 * on (part 3's promises restored with their switches), or in an upgrade only the new promises.
 */
function Promises({ features, upgrade, byCode }: { features: Record<Microsoft365Feature, boolean>; upgrade: readonly Microsoft365Feature[] | null; byCode: boolean }) {
  const { t } = useTranslation();
  const canId = useId();
  const cannotId = useId();
  const promises = connectPromises(features);
  const can: string[] = upgrade ? upgradePromises(upgrade) : promises.can;
  return (
    <>
      <div className="m365-promises">
        <section className="m365-promises__column m365-promises__column--can" aria-labelledby={canId}>
          <h3 id={canId} className="m365-promises__title">
            {t(upgrade ? 'microsoft365.user.dialog.upgrade.can' : 'microsoft365.user.dialog.can')}
          </h3>
          <ul className="m365-promises__list" data-testid="microsoft365-can">
            {can.map((item) => (
              <li key={item}>
                <Icons.check size={16} />
                <span>{t(`microsoft365.user.dialog.canItems.${item}`)}</span>
              </li>
            ))}
          </ul>
        </section>
        <section className="m365-promises__column m365-promises__column--cannot" aria-labelledby={cannotId}>
          <h3 id={cannotId} className="m365-promises__title">
            {t('microsoft365.user.dialog.cannot')}
          </h3>
          <ul className="m365-promises__list" data-testid="microsoft365-cannot">
            {([promises.send, 'delete', 'password', 'others'] as const).map((item) => (
              <li key={item}>
                <Icons.close size={16} />
                <span>{t(`microsoft365.user.dialog.cannotItems.${item}`)}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
      <p className="m365-dialog__note">
        <Trans i18nKey={byCode ? 'microsoft365.user.dialog.noteDevice' : 'microsoft365.user.dialog.noteRedirect'} components={{ b: <strong /> }} />
      </p>
    </>
  );
}

/** The device code (Kodla): the code with "Kopyala", the three steps, waiting; or why it stopped. */
function DeviceCode({ device, status, reason, error }: { device: Microsoft365DeviceStart; status: string; reason: string | null; error: unknown }) {
  const { t, i18n } = useTranslation();
  const zone = useCompanyZone();
  const until = new Date(device.expiresAt);
  const time = Number.isNaN(until.getTime()) ? null : until.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit', timeZone: zone });
  return (
    <div className="m365-device" data-testid="microsoft365-device" data-status={status}>
      {status === 'pending' ? (
        <>
          <p className="m365-device__intro">{t('microsoft365.user.dialog.device.intro')}</p>
          <div className="m365-device__row">
            <code className="m365-device__code" data-testid="microsoft365-device-code">
              {device.userCode}
            </code>
            <span data-testid="microsoft365-device-copy">
              <CopyButton text={device.userCode} />
            </span>
          </div>
          <ol className="m365-device__steps">
            <li>{t('microsoft365.user.dialog.device.steps.open')}</li>
            <li>{t('microsoft365.user.dialog.device.steps.enter')}</li>
            <li>{t('microsoft365.user.dialog.device.steps.done')}</li>
          </ol>
          <p className="m365-device__wait" role="status" data-testid="microsoft365-device-waiting">
            <span className="ui-spinner ui-spinner--inline" aria-hidden="true" />
            <span>{t('microsoft365.user.dialog.device.waiting')}</span>
            {time ? <span className="m365-device__until">{t('microsoft365.user.dialog.device.until', { time })}</span> : null}
          </p>
        </>
      ) : status === 'expired' ? (
        <Alert tone="warning" title={t('microsoft365.user.dialog.device.expired')} data-testid="microsoft365-device-expired" />
      ) : status === 'declined' ? (
        <Alert tone="warning" title={t('microsoft365.user.dialog.device.declined')} data-testid="microsoft365-device-declined" />
      ) : error ? (
        <ErrorAlert error={error} />
      ) : status === 'failed' ? (
        <Alert tone="error" title={t('microsoft365.user.result.failed')} data-testid="microsoft365-device-failed">
          <p>{t(`microsoft365.user.reasons.${reasonKey(reason)}`)}</p>
          {reasonKey(reason) === 'consent_required' ? <ConsentRequestButton /> : null}
        </Alert>
      ) : null}
    </div>
  );
}
